import { PROPOSAL_STATUSES } from "../constants/proposalStatuses";
import { SavedProposal } from "../types/proposal";
import { formatCurrency } from "./currency";
import { normalizeOperationalDetails } from "./proposalOperationalDetails";

const valueOrDash = (value: string): string => value.trim() || "—";

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });

const formatDate = (value: string): string =>
  value ? new Date(`${value}T12:00:00`).toLocaleDateString("pt-BR") : "—";

const formatAddress = (
  address: ReturnType<
    typeof normalizeOperationalDetails
  >["shipping"]["address"],
): string => {
  const street = [address.street, address.number].filter(Boolean).join(", ");
  return (
    [
      street,
      address.complement,
      address.neighborhood,
      [address.city, address.state].filter(Boolean).join("/"),
      address.zipCode,
    ]
      .filter(Boolean)
      .join(" — ") || "—"
  );
};

export const generateNotionNote = (proposal: SavedProposal): string => {
  const { client } = proposal.draft;
  const products = proposal.draft.products.filter(product => product.selected);
  const currentStatusIndex = PROPOSAL_STATUSES.indexOf(proposal.status);
  const operational = normalizeOperationalDetails(proposal.operationalDetails);
  const productRows = products
    .map(
      product =>
        `| ${product.name} | ${product.quantity} | ${formatCurrency(
          product.unitPrice,
        )} | ${formatCurrency(product.subtotal)} |`,
    )
    .join("\n");
  const pipeline = PROPOSAL_STATUSES.map((status, index) => {
    const checked = index <= currentStatusIndex ? "x" : " ";
    const current = status === proposal.status ? " **(atual)**" : "";
    return `- [${checked}] ${status}${current}`;
  }).join("\n");

  return `# Proposta Comercial — ${valueOrDash(client.name)}

**Código:** ${proposal.id}  
**Status atual:** ${proposal.status}  
**Criada em:** ${formatDateTime(proposal.createdAt)}  
**Atualizada em:** ${formatDateTime(proposal.updatedAt)}

## Cliente

- **Nome:** ${valueOrDash(client.name)}
- **Empresa:** ${valueOrDash(client.company)}
- **E-mail:** ${valueOrDash(client.email)}
- **Telefone:** ${valueOrDash(client.phone)}
- **CPF/CNPJ:** ${valueOrDash(client.document ?? "")}
- **Origem do cadastro:** ${
    client.source === "user"
      ? "Usuário Noar"
      : client.source === "asaas"
      ? "Cliente Asaas"
      : "Manual"
  }
- **ID do usuário Noar:** ${valueOrDash(client.userId ?? "")}
- **ID do cliente Asaas:** ${valueOrDash(client.asaasCustomerId ?? "")}
- **CEP:** ${valueOrDash(client.zipCode)}
- **Endereço:** ${client.address ? formatAddress(client.address) : "—"}

## Produtos

| Produto | Quantidade | Valor unitário | Subtotal |
| --- | ---: | ---: | ---: |
${productRows || "| Nenhum produto selecionado | — | — | — |"}

## Valores

- **Subtotal:** ${formatCurrency(proposal.totals.subtotal)}
- **Frete:** ${formatCurrency(proposal.totals.freight)}
- **Desconto:** ${formatCurrency(proposal.totals.discount)}
- **Total:** ${formatCurrency(proposal.totals.total)}

## Pagamento e cobrança

- **Forma de pagamento:** ${valueOrDash(operational.payment.method)}
- **Link de pagamento:** ${valueOrDash(operational.payment.paymentLink)}
- **Gerado em nome de:** ${valueOrDash(operational.payment.payerName)}
- **CPF/CNPJ do pagador:** ${valueOrDash(operational.payment.payerDocument)}
- **Vencimento:** ${formatDate(operational.payment.dueDate)}
- **Pago em:** ${formatDate(operational.payment.paidAt)}
- **Endereço de cobrança:** ${formatAddress(operational.billing.address)}

## Envio

- **Destinatário:** ${valueOrDash(operational.shipping.recipientName)}
- **Telefone:** ${valueOrDash(operational.shipping.recipientPhone)}
- **Endereço de envio:** ${formatAddress(operational.shipping.address)}
- **Transportadora/Correios:** ${valueOrDash(operational.delivery.carrier)}
- **Código de rastreamento:** ${valueOrDash(operational.delivery.trackingCode)}
- **Envio solicitado em:** ${formatDate(operational.delivery.requestedAt)}
- **Pedido enviado em:** ${formatDate(operational.delivery.shippedAt)}

## Andamento

${pipeline}

## Observações

${operational.notes || "-"}`;
};
